import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useAuth } from "../auth/AuthContext";
import { ordersApi, type Order } from "../api/orders";
import { dashboardApi, type SalesDashboard as SalesDashboardData } from "../api/dashboard";
import { flagEmoji } from "../i18n/countries";
import { KpiWidget } from "../components/KpiWidget";
import { formatMoney, formatNumber as brojLokalno } from "../lib/format";
import { fmtDate } from "./finance/money";
import {
  BarChart, Donut, HorizontalBars, KpiCard, LedgerTable, Muted, Section,
  actionLink, countryBars, kpiStrip, nazivMeseca, relTime, reviewItem, reviewList, reviewStatusChip,
} from "./dashboard/primitives";
/**
 * Kontrolna tabla prodaje, iz dokumenata koje portal drži.
 *
 * Do prenosa finansija ovde su brojevi bili izmišljeni i tako i označeni: prihod, tok
 * porudžbina i dužnici izvodili su se iz mejla korisnika da bi svaki komercijalista video
 * stabilan ekran. Posle uvoza (83.470 računa, 44.307 porudžbina) sve to ima svoj izvor, pa
 * demo oznake nema nigde — nema ni izmišljenih brojeva iza njih.
 *
 * Ispunjenje kvote je **uklonjeno, ne popravljeno**: imenilac mu je cilj, a tabela prognoza
 * nema nijedan red. Isto pravilo kao kod pregleda saradnje (odluka G4) — prazno je poštenije
 * od izmišljenog. Na to mesto je stalo dospelo potraživanje, koje postoji i koje se gleda.
 *
 * Iznosi su u evrima, preračunati po kursu na dan dokumenta; tok porudžbina je broj, ne iznos,
 * jer porudžbina nema svoju valutu pa se preko kupaca ne može sabrati.
 */
export default function SalesDashboard() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const { state, hasPermission } = useAuth();
  const canFinance = hasPermission("section:customer_finance:read");
  const canOrders = hasPermission("section:orders:read");

  const [orders, setOrders] = useState<Order[] | null>(null);
  const [data, setData] = useState<SalesDashboardData | null>(null);

  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setData(null); setError(null);
    // Kontrolna tabla gleda skorašnje porudžbine, ne celu istoriju.
    if (canOrders) void ordersApi.list({ size: 200 }).then((r) => { if (live) setOrders(r.content); }).catch((e) => { if (live) { setOrders([]); setError(e.message); } });
    else setOrders(null);
    if (canFinance || canOrders) void dashboardApi.sales(12).then((result) => { if (live) setData(result); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [canFinance, canOrders]);
  const showFinance = canFinance && !!data && data.financeVisible !== false;
  const showOrders = canOrders && !!data && data.ordersVisible !== false;

  const [period, setPeriod] = useState<3 | 6 | 12>(12);
  const [pipelineFilter, setPipelineFilter] = useState<string | null>(null);

  const novac = (n: number) => formatMoney(n, "EUR", locale, { maximumFractionDigits: 0 });
  // The board is counted up to the day the outgoing invoices reach, which can be earlier than today;
  // the tiles that compare periods name that day, so the comparison is not read as reaching today.
  const podaciDo = data?.dataUntil
    ? t("dashboard.sales.kpi.dataUntil", "podaci do {{datum}}", { datum: fmtDate(data.dataUntil) })
    : undefined;
  const nijeUracunato = data && ((data.carriedPostedEur ?? 0) > 0 || (data.otherPostedEur ?? 0) > 0)
    ? t("finance.notInvoiced", "Nije uračunato: preneto iz ranijih godina {{prenos}}, ostala knjiženja {{ostalo}}",
      { prenos: novac(data.carriedPostedEur ?? 0), ostalo: novac(data.otherPostedEur ?? 0) })
    : undefined;
  const mesecni = useMemo(
    () => (data?.monthlyInvoiced ?? []).map((m) => ({ label: nazivMeseca(m.label, locale), value: m.value })),
    [data, locale],
  );
  const tokPoFazi = useMemo(
    () => (data?.pipeline ?? []).map((p) => ({
      label: statusNaziv(p.status, t), value: p.orders, color: statusBoja(p.status),
    })),
    [data, t],
  );

  if (state.status !== "authenticated") return null;
  const { user } = state;
  const hour = new Date().getHours();
  const timeGreetingKey =
    hour < 12 ? "dashboard.morning" : hour < 18 ? "dashboard.afternoon" : "dashboard.evening";
  const timeGreetingFallback =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const pipelineTotal = tokPoFazi.reduce((sum, p) => sum + p.value, 0);

  const reviewOrders = (orders ?? [])
    .filter((o) => o.status === "SUBMITTED" || o.status === "IN_REVIEW")
    .sort((a, b) => new Date(b.submittedAt ?? b.createdAt).getTime() - new Date(a.submittedAt ?? a.createdAt).getTime())
    .slice(0, 6);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("dashboard.breadcrumb.aria", "Breadcrumb")}>
            <span>{t("nav.home", "Home")}</span>
            <span>{t("nav.dashboard", "Dashboard")}</span>
          </nav>
          <h1>{t(timeGreetingKey, { defaultValue: timeGreetingFallback })}, {user.firstName}</h1>
          <div className="sub">{t("dashboard.sales.subtitle", "What needs your attention today")}</div>
        </div>
      </div>

      {hasPermission("section:kpi:read") && <KpiWidget />}
      {error && <div className="tag err" role="alert">{error}</div>}
      {!data && !error && <p role="status">{t("common.loading", "Loading…")}</p>}

      <div style={kpiStrip}>
        {/* Invoices only. The opening balance and the other postings are debits too, but summed
            as sales they would compare a year that carries an opening balance with one that does
            not, so they are named under the value instead of hidden. */}
        {showFinance && data.invoicedYtd !== null && <KpiCard
          delay={0}
          label={t("dashboard.sales.kpi.salesYtd", "Fakturisano od početka godine, sa PDV-om")}
          value={data?.invoicedYtd ?? 0}
          format={novac}
          delta={data?.invoicedYtdDeltaPct ?? undefined}
          deltaLabel={data?.invoicedYtdDeltaPct == null
            ? t("dashboard.sales.kpi.noBase", "lane nije bilo prometa") : undefined}
          note={nijeUracunato || podaciDo
            ? <>
              {nijeUracunato && <div>{nijeUracunato}</div>}
              {podaciDo && <div>{podaciDo}</div>}
            </>
            : undefined}
          spark={mesecni.map((m) => m.value)}
          color="var(--brand-primary)"
        />}
        {showOrders && data.ordersThisMonth !== null && <KpiCard
          delay={1}
          label={t("dashboard.sales.kpi.ordersMonth", "Porudžbine ovog meseca")}
          value={data?.ordersThisMonth ?? 0}
          format={(v) => brojLokalno(v, locale, { maximumFractionDigits: 0 })}
          delta={data?.ordersDeltaPct ?? undefined}
          note={podaciDo}
          color="var(--tone-cyan)"
        />}
        {/* Na mestu ispunjenja kvote: dospelo potraživanje. Kvota je tražila cilj, a prognoza
            nema nijedan red; dospelo postoji i gleda se svakog jutra.

            Pravilo: iznos je zbir preko celog obima koji korisnik vidi, a ne zbir spiska
            dužnika ispod — taj spisak je odsečen, pa bi zbir preko njega bio manji od istine.
            Sitan tekst nosi broj kupaca i, kad obim nije ceo, čiji su kupci: iznos za uži obim
            bez te oznake izgleda kao ukupan dug firme. */}
        {showFinance && data.overdueEur !== null && <KpiCard
          delay={2}
          label={t("dashboard.sales.kpi.overdue", "Dospelo potraživanje")}
          value={data?.overdueEur ?? 0}
          format={novac}
          deltaLabel={data?.overdueScopeLimited
            ? t("dashboard.sales.kpi.overdueMine", "vaših kupaca sa dospelim dugom: {{n}}",
              { n: brojLokalno(data?.overdueCompanies ?? 0, locale, { maximumFractionDigits: 0 }) })
            : t("dashboard.sales.kpi.overdueAll", "kupaca sa dospelim dugom: {{n}}",
              { n: brojLokalno(data?.overdueCompanies ?? 0, locale, { maximumFractionDigits: 0 }) })}
          deltaTone={(data?.overdueEur ?? 0) > 0 ? "warn" : "ok"}
          note={data.dataUntil
            ? t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.dataUntil) })
            : undefined}
          color="var(--warn)"
        />}
      </div>

      {showOrders && <Section
        title={t("dashboard.sales.review.title", "Orders awaiting your review")}
        action={
          <Link to="/orders" style={actionLink}>
            {reviewOrders.length === 0
              ? t("dashboard.sales.viewAll", "View all")
              : t("dashboard.sales.review.viewAll", "View all ({{count}})", { count: reviewOrders.length })} →
          </Link>
        }
      >
        {orders === null ? (
          <Muted>{t("common.loading", "Loading…")}</Muted>
        ) : reviewOrders.length === 0 ? (
          <Muted>
            {t("dashboard.sales.review.empty", "Inbox zero. No orders are currently waiting on you — well done.")}
          </Muted>
        ) : (
          <ul style={reviewList}>
            {reviewOrders.map((o) => {
              const fresh =
                !!o.submittedAt
                && Date.now() - new Date(o.submittedAt).getTime() < 24 * 3600 * 1000;
              return (
                <li key={o.id} style={reviewItem}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {fresh && (
                      <span
                        className="ord-fresh-dot"
                        aria-label={t("orders.row.freshLabel", "New")}
                        title={t("orders.row.freshTitle", "New — submitted in the last 24h")}
                      />
                    )}
                    <span style={{ fontSize: 18 }}>{o.companyCountry ? flagEmoji(o.companyCountry) : "🏳"}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.companyName}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--mono)" }}>
                        {o.orderNumber} · {o.lines.length === 1
                          ? t("orderEditor.lines.count_one", "{{count}} line", { count: o.lines.length })
                          : t("orderEditor.lines.count_other", "{{count}} lines", { count: o.lines.length })}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={reviewStatusChip(o.status === "SUBMITTED" ? "submit" : "review")}>
                      {o.status === "SUBMITTED" ? t("orders.status.submitted", "Submitted") : t("orders.status.inReview", "In review")}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                      {relTime(o.submittedAt ?? o.createdAt, locale, t)}
                    </span>
                    <Link to={`/orders/${o.id}`} className="btn sm" style={{ textDecoration: "none" }}>
                      {t("dashboard.sales.review.action", "Review")} →
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>}

      <div className="dash-two-col">
        {showFinance && <Section
          title={t("dashboard.sales.charts.monthlyRevenue", "Fakturisano po mesecima")}
          action={
            <div style={{ display: "flex", gap: 4 }}>
              {[3, 6, 12].map((m) => (
                <button key={m} type="button" className="sd-tab-btn" aria-pressed={period === m}
                        onClick={() => setPeriod(m as 3 | 6 | 12)}>
                  {m}m
                </button>
              ))}
            </div>
          }
        >
          {mesecni.length === 0 ? (
            <Muted>{t("dashboard.sales.charts.noInvoices", "Nema faktura u ovom periodu.")}</Muted>
          ) : (
            <BarChart data={mesecni.slice(-period)} color="var(--brand-primary)" formatValue={novac} />
          )}
        </Section>}

        {showOrders && <Section
          title={t("dashboard.sales.charts.pipeline", "Porudžbine po statusu")}
          action={pipelineFilter && (
            <button type="button" className="sd-tab-btn" onClick={() => setPipelineFilter(null)}>
              {t("dashboard.sales.charts.clearFilter", "Clear filter")}
            </button>
          )}
        >
          {tokPoFazi.length === 0 ? (
            <Muted>{t("dashboard.sales.charts.noOrders", "Nema porudžbina u ovoj godini.")}</Muted>
          ) : (
            <Donut
              slices={tokPoFazi}
              highlight={pipelineFilter}
              onSliceClick={(label) => setPipelineFilter(pipelineFilter === label ? null : label)}
              centerLabel={brojLokalno(
                pipelineFilter
                  ? (tokPoFazi.find((p) => p.label === pipelineFilter)?.value ?? 0)
                  : pipelineTotal,
                locale, { maximumFractionDigits: 0 })}
              centerSub={pipelineFilter ?? t("dashboard.sales.charts.pipelineOrders", "porudžbina")}
              formatValue={(v) => brojLokalno(v, locale, { maximumFractionDigits: 0 })}
            />
          )}
        </Section>}
      </div>

      {/* Pravilo: naslov obećava onoliko koliko tabela pokazuje. Tabela drži nekoliko najvećih
          dužnika, pa i naslov kaže „najveći", a ispod stoji koliko je redova od koliko dužnika,
          uz vezu na finansije gde su svi. */}
      {showFinance && <Section
        title={t("dashboard.sales.debtors.titleTop", "Najveći dužnici")}
        action={
          <Link to="/finance" style={actionLink}>
            {t("dashboard.sales.viewAll", "Prikaži sve")} →
          </Link>
        }
      >
        {data === null ? (
          <Muted>{t("common.loading", "Učitavanje…")}</Muted>
        ) : data.topDebtors.length === 0 ? (
          <Muted>{t("dashboard.sales.debtors.empty", "Nema dospelih računa. Kupci plaćaju na vreme.")}</Muted>
        ) : (
          <>
            <LedgerTable
              rows={data.topDebtors}
              locale={locale}
              companyLabel={t("finance.overview.company", "Kupac")}
              hrefFor={(d) => `/finance/companies/${d.companyId}`}
            />
            {(data.overdueCompanies ?? 0) > data.topDebtors.length && (
              <Muted>
                {t("dashboard.sales.debtors.cut", "Prikazano {{prikazano}} od {{svih}} dužnika.", {
                  prikazano: data.topDebtors.length,
                  svih: brojLokalno(data.overdueCompanies ?? 0, locale, { maximumFractionDigits: 0 }),
                })}
              </Muted>
            )}
          </>
        )}
      </Section>}

      <div className="dash-two-col">
        {showFinance && <Section
          title={t("dashboard.sales.charts.topCompanies", "Najveći kupci (fakturisano ove godine)")}
          action={hasPermission("section:companies:read") && <Link to="/admin/companies" style={actionLink}>{t("dashboard.sales.viewAll", "Prikaži sve")} →</Link>}
        >
          {(data?.topCustomers.length ?? 0) === 0 ? (
            <Muted>{t("dashboard.sales.companies.empty", "Ove godine još nije fakturisano nijednom kupcu u vašem opsegu.")}</Muted>
          ) : (
            <HorizontalBars
              data={(data?.topCustomers ?? []).map((c) => ({
                label: `${c.country ? flagEmoji(c.country) + " " : ""}${c.name}`, value: c.amountEur,
              }))}
              max={data?.topCustomers[0]?.amountEur ?? 1}
              format={novac}
            />
          )}
        </Section>}

        {showFinance && <Section title={t("dashboard.sales.charts.byCountry", "Fakturisano po državama")}>
          {(data?.byCountry.length ?? 0) === 0 ? (
            <Muted>{t("dashboard.sales.charts.byCountryEmpty", "Nema fakturisanog prometa u ovoj godini.")}</Muted>
          ) : (
            <HorizontalBars
              data={countryBars(data?.byCountry ?? [], locale)}
              max={data?.byCountry[0]?.value ?? 1}
              format={novac}
            />
          )}
        </Section>}
      </div>

    </div>
  );
}
/**
 * Status porudžbine na jeziku korisnika.
 *
 * Isti prevodi koje koristi i spisak porudžbina; kontrolna tabla ne sme da ih zove drugačije,
 * jer se sa nje klikom ide baš na taj spisak.
 */
function statusNaziv(status: string, t: TFunction): string {
  const kljuc: Record<string, [string, string]> = {
    DRAFT: ["orders.status.draft", "Nacrt"],
    SUBMITTED: ["orders.status.submitted", "Poslata"],
    IN_REVIEW: ["orders.status.inReview", "Na pregledu"],
    APPROVED: ["orders.status.approved", "Odobrena"],
    REJECTED: ["orders.status.rejected", "Odbijena"],
    SHIPPED: ["orders.status.shipped", "Isporučena"],
    INVOICED: ["orders.status.invoiced", "Fakturisana"],
    CANCELLED: ["orders.status.cancelled", "Otkazana"],
  };
  const par = kljuc[status];
  return par ? t(par[0], { defaultValue: par[1] }) : status;
}

/** Boja prati put dokumenta: nacrt siv, na pregledu topao, odobreno i dalje hladno pa zeleno. */
function statusBoja(status: string): string {
  switch (status) {
    case "DRAFT": return "var(--ink-4)";
    case "SUBMITTED": return "var(--info)";
    case "IN_REVIEW": return "var(--warn)";
    case "APPROVED": return "var(--brand-primary)";
    case "SHIPPED": return "var(--tone-cyan)";
    case "INVOICED": return "var(--ok)";
    case "REJECTED":
    case "CANCELLED": return "var(--err)";
    default: return "var(--ink-3)";
  }
}
